import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Crown } from 'lucide-react';
import { startOfMonth, endOfDay } from 'date-fns';
import { salesApi } from '@/services/api';
import { formatCurrency } from '@/lib/utils';
import type { SalesSummary } from '@/types';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  color: string;
  alpha: number;
  size: number;
  decay: number;
}

interface Firework {
  x: number;
  y: number;
  targetY: number;
  speed: number;
  color: string;
  exploded: boolean;
  particles: Particle[];
}

const COLORS = [
  '#FFD700', // Gold
  '#FFA500', // Orange
  '#FF6B6B', // Red
  '#DDA0DD', // Plum
  '#BB8FCE', // Purple
  '#F7DC6F', // Yellow
  '#85C1E9', // Sky
  '#4ECDC4', // Teal
];

function FireworksCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const fireworksRef = useRef<Firework[]>([]);
  const lastLaunchRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.scale(dpr, dpr);
    };
    resize();

    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    const launch = (): Firework => {
      const rect = canvas.getBoundingClientRect();
      return {
        x: Math.random() * rect.width * 0.8 + rect.width * 0.1,
        y: rect.height,
        targetY: Math.random() * rect.height * 0.4 + rect.height * 0.1,
        speed: 2 + Math.random() * 1.5,
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        exploded: false,
        particles: [],
      };
    };

    const explode = (fw: Firework) => {
      const count = 30 + Math.floor(Math.random() * 20);
      for (let i = 0; i < count; i++) {
        const angle = (Math.PI * 2 * i) / count + Math.random() * 0.2;
        const speed = 0.8 + Math.random() * 2;
        fw.particles.push({
          x: fw.x,
          y: fw.y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          color: Math.random() > 0.3 ? fw.color : COLORS[Math.floor(Math.random() * COLORS.length)],
          alpha: 1,
          size: 1.2 + Math.random() * 1.2,
          decay: 0.018 + Math.random() * 0.012,
        });
      }
      fw.exploded = true;
    };

    const tick = () => {
      const rect = canvas.getBoundingClientRect();

      ctx.fillStyle = 'rgba(20, 10, 40, 0.18)';
      ctx.fillRect(0, 0, rect.width, rect.height);

      const now = Date.now();
      if (now - lastLaunchRef.current > 600 + Math.random() * 700) {
        fireworksRef.current.push(launch());
        lastLaunchRef.current = now;
      }

      fireworksRef.current = fireworksRef.current.filter((fw) => {
        if (!fw.exploded) {
          fw.y -= fw.speed;
          ctx.beginPath();
          ctx.arc(fw.x, fw.y, 1.5, 0, Math.PI * 2);
          ctx.fillStyle = fw.color;
          ctx.fill();
          if (fw.y <= fw.targetY) explode(fw);
          return true;
        }
        let alive = 0;
        fw.particles.forEach((p) => {
          if (p.alpha <= 0) return;
          p.x += p.vx;
          p.y += p.vy;
          p.vy += 0.04;
          p.vx *= 0.99;
          p.alpha -= p.decay;
          if (p.alpha > 0) {
            alive++;
            ctx.beginPath();
            ctx.arc(p.x, p.y, p.size * p.alpha, 0, Math.PI * 2);
            ctx.fillStyle = p.color;
            ctx.globalAlpha = p.alpha;
            ctx.fill();
            ctx.globalAlpha = 1;
          }
        });
        return alive > 0;
      });

      animationRef.current = requestAnimationFrame(tick);
    };

    tick();

    return () => {
      ro.disconnect();
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      fireworksRef.current = [];
    };
  }, []);

  return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />;
}

export function TopAgentMonthCard() {
  const { t, i18n } = useTranslation();

  const { data: monthSummary, isLoading } = useQuery<SalesSummary>({
    queryKey: ['sales-summary-month'],
    queryFn: () => {
      const now = new Date();
      return salesApi.getSummary({
        preset: 'this_month',
        startDate: startOfMonth(now).toISOString(),
        endDate: endOfDay(now).toISOString(),
      });
    },
    refetchInterval: 60000,
  });

  const topAgent = monthSummary?.agentSummaries?.[0];

  if (isLoading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 animate-pulse">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
          <div className="flex-1">
            <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
            <div className="h-6 bg-gray-200 rounded w-3/4"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden rounded-xl shadow-sm border border-purple-300 p-6 bg-gradient-to-br from-purple-900 via-indigo-900 to-purple-900">
      <FireworksCanvas />
      <div className="relative flex items-center gap-4">
        <div className="p-3 rounded-lg bg-yellow-400/20 ring-1 ring-yellow-300/40 backdrop-blur-sm">
          <Crown className="w-6 h-6 text-yellow-300 drop-shadow-[0_0_8px_rgba(253,224,71,0.8)]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-yellow-100/90 truncate">
            {t('dashboard.topAgentMonth')}
          </p>
          <p className="text-xl sm:text-2xl font-bold text-white whitespace-nowrap drop-shadow-[0_0_10px_rgba(255,255,255,0.4)]">
            {topAgent?.agentName || '-'}
          </p>
          {topAgent && (
            <p className="text-sm text-yellow-200/90 whitespace-nowrap">
              {formatCurrency(topAgent.totalSales, i18n.language)}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
